import { parseEuroCents } from '../money'
import { type HandOverForm, inspectionReason, readingKm } from './readiness'

/** The most one charge can be, in cents (the API's own limit). */
export const MAX_CHARGE_CENTS = 10_000_000

/**
 * What the return form still lacks, each said beside the button (DESIGN.md:
 * never a disabled button with no reason), in the page's order. Money owed is
 * not one: the server takes the vehicle back whatever the account says.
 */
export type ReturnReason =
  | 'km'
  | 'kmBelow'
  | 'fuel'
  | 'reading'
  | 'unsent'
  | 'unsentReason'
  | 'charges'

export interface ReturnForm {
  /** The hand-over's reading: the server refuses a lower one (`km_below_checkout`). */
  kmOut: number
  km: string
  fuel: string
  /** S4: this iPad's items of this inspection not yet on the server. */
  inspection: HandOverForm['inspection']
  /** The charges typed at the counter, each with what it is for and how much. */
  charges: readonly { description: string; amount: string }[]
}

export interface ReturnReadiness {
  reasons: ReturnReason[]
  ready: boolean
}

/** A charge typed as the server takes it: words, and an amount from a cent to the limit. */
export function chargeComplete(charge: { description: string; amount: string }): boolean {
  const cents = parseEuroCents(charge.amount)
  return (
    charge.description.trim() !== '' && cents !== null && cents > 0 && cents <= MAX_CHARGE_CENTS
  )
}

/** Whether the vehicle can be taken back, as the server will answer it, and why not. */
export function returnReadiness(form: ReturnForm): ReturnReadiness {
  const reasons: ReturnReason[] = []
  const km = readingKm(form.km)
  if (km === null) reasons.push('km')
  else if (km < form.kmOut) reasons.push('kmBelow')
  if (form.fuel === '') reasons.push('fuel')
  const inspection = inspectionReason(form.inspection)
  if (inspection) reasons.push(inspection)
  if (!form.charges.every(chargeComplete)) reasons.push('charges')
  return { reasons, ready: reasons.length === 0 }
}
